/**
 * Store-level Google rating badge (cart drawer + PDP), per Gokhan's decision
 * 2026-09-27. Deliberately says "w Google" / "on Google" -- this is the
 * Kraków café's store/location rating from the already-installed "Google
 * Review Plus by Entangle" app, NOT a per-product review rating (see the
 * separate, currently-dormant product.metafields.reviews.rating block in
 * snippets/product-information.liquid -- do not confuse the two).
 *
 * Pulled dynamically where possible: that app renders window.grp_app_metadata
 * (rating, total_num_of_reviews) as an inline script, but only on pages where
 * its own app block is placed (currently just the homepage section in
 * templates/index.json) -- there's no sitewide app embed for it, and
 * templates/*.json is off-limits to edit here per the operating rules. So
 * this fetches the homepage document in the background and regex-extracts
 * just the two numbers (not a full JSON.parse -- the metadata blob also
 * contains a large, potentially messy reviews array we don't need), cached
 * in sessionStorage for the rest of the visit.
 *
 * Falls back to a hardcoded value if that fetch/parse ever fails (app
 * uninstalled, block removed from the homepage, network error). That
 * fallback goes stale silently if it's ever what's actually showing --
 * update FALLBACK below monthly; see DECISIONS.md 2026-09-27.
 */
(function () {
  // Last confirmed live 2026-09-27 via the app's own window.grp_app_metadata.
  // Update monthly if the dynamic pull below ever falls back to this.
  var FALLBACK = { rating: '4.7', count: 718 };
  var PLACE_ID = 'ChIJ_fCcnSpbFkcRhRPivLGnywY';
  var PROFILE_URL = 'https://www.google.com/maps/place/?q=place_id:' + PLACE_ID;
  var CACHE_KEY = 'pb_google_rating_v1';

  function plNoun(n) {
    if (n === 1) return 'opinia';
    var mod10 = n % 10, mod100 = n % 100;
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'opinie';
    return 'opinii';
  }
  function enNoun(n) { return n === 1 ? 'review' : 'reviews'; }
  function trNoun() { return 'değerlendirme'; }

  function noun(n) {
    var lang = document.documentElement.lang || 'pl';
    if (lang.indexOf('en') === 0) return enNoun(n);
    if (lang.indexOf('tr') === 0) return trNoun(n);
    return plNoun(n);
  }

  function formatRating(rating) {
    var lang = document.documentElement.lang || 'pl';
    return lang.indexOf('en') === 0 ? String(rating) : String(rating).replace('.', ',');
  }

  function render(el, rating, count) {
    var template = window.PB_GOOGLE_RATING_STRING || '★ {{ rating }} · {{ count }} {{ noun }} w Google';
    var html = template
      .replace('{{ rating }}', formatRating(rating))
      .replace('{{ count }}', count)
      .replace('{{ noun }}', noun(count));
    var renderedHtml = '<a href="' + PROFILE_URL + '" target="_blank" rel="noopener" ' +
      'style="font-size:0.8em;color:#605d49;letter-spacing:0.01em;text-decoration:none;">' +
      html + '</a>';

    // This badge sits inside the cart drawer watched by drawer-value-boost.js.
    // Replacing identical HTML still creates a childList mutation, which used
    // to restart the drawer initializer forever on every page with a non-empty
    // cart. Keep the renderer idempotent so an unchanged badge does no DOM work.
    if (el.__pbGoogleRatingHtml === renderedHtml || el.innerHTML === renderedHtml) {
      el.__pbGoogleRatingHtml = renderedHtml;
      return false;
    }
    el.__pbGoogleRatingHtml = renderedHtml;
    el.innerHTML = renderedHtml;
    return true;
  }

  function getLiveMetadata() {
    try {
      var cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) {}

    return fetch('/').then(function (r) {
      return r.ok ? r.text() : null;
    }).then(function (html) {
      if (!html) return null;
      var ratingMatch = html.match(/"rating":"([\d.]+)"/);
      var countMatch = html.match(/"total_num_of_reviews":(\d+)/);
      if (!ratingMatch || !countMatch) return null;
      var result = { rating: ratingMatch[1], count: parseInt(countMatch[1], 10) };
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(result)); } catch (e) {}
      return result;
    }).catch(function () { return null; });
  }

  function init(el) {
    getLiveMetadata().then(function (live) {
      var rating = live ? live.rating : FALLBACK.rating;
      var count = live ? live.count : FALLBACK.count;
      render(el, rating, count);
    });
  }

  function renderAll() {
    document.querySelectorAll('[data-google-rating-badge]').forEach(init);
  }

  // Exposed so drawer-value-boost.js's own init() (already re-run on every
  // theme-driven cart-drawer redraw) can re-render this badge too, instead of
  // this file adding a second competing MutationObserver on the same element.
  window.PBGoogleRatingBadge = { renderAll: renderAll };

  document.addEventListener('DOMContentLoaded', renderAll);
})();
